import { HoverPopover } from '@app/components/base/HoverPopover';
import { getSpeakerKey, getVoiceLabelInitials } from '@app/modules/diarization/speaker-utils';
import { PersonAvatar } from '@app/modules/persons/components/PersonAvatar';
import { usePerson } from '@app/modules/persons/hooks/usePerson';
import { DiarizationFormattedType } from '@app/modules/search/types';
import { toTimestamp } from '@mi-front/core';
import { StreamEl } from '@mi-front/ui';
import { Box } from '@mui/material';
import { useContext } from 'react';
import { DiarizationContext } from '../../../diarization/DiarizationContext';
import { DiarizationInfo } from '../diarization/DiarizationInfo';
import { DiarizationMarkerLabel } from './elements';

type DiarizationMarkerProps = {
  readonly diarization: NonNullable<DiarizationFormattedType>;
};

/**
 * Renders a diarization (speaker) marker on the focus strip. Uses MUI Popover
 * via HoverPopover so the info popup appears above other strips (e.g. Live Topics).
 * With person_id: PersonAvatar (Person icon while loading, then image/initials).
 * Without person_id: voice_label initials.
 */
export function DiarizationMarker({ diarization }: DiarizationMarkerProps) {
  const { speakers } = useContext(DiarizationContext);
  const person = usePerson(diarization.person_id);
  const hasPersonId = !!diarization.person_id;

  const speakerKey = getSpeakerKey(diarization);
  const speaker = speakers.find((item) => item.key === speakerKey);
  const color = speaker?.color ?? '#00BCD4';
  const fallbackName = person?.name ?? diarization.voice_label ?? diarization.label ?? '';

  return (
    <StreamEl
      key={diarization.time}
      ts={toTimestamp(diarization.time) + diarization.segment.start * 1000}
      offsetX={-18}
      offsetY={-2}
      position="bottom"
      zIndex={2}
    >
      {/*
        Block wrapper locks StreamEl height. HoverPopover wraps the trigger in an
        inline-flex span; when Avatar swaps icon → img/initials the inline baseline
        changes and bottom-anchored markers jump up a few pixels.
      */}
      <Box sx={{ display: 'block', width: 36, height: 36, lineHeight: 0 }}>
        <HoverPopover
          trigger={
            <DiarizationMarkerLabel color={color}>
              {hasPersonId ? (
                <PersonAvatar
                  person={person}
                  fallbackName={fallbackName}
                  size="medium"
                  sx={{ width: 32, height: 32, bgcolor: color }}
                />
              ) : (
                <Box sx={{ typography: 'caption', lineHeight: 1 }}>
                  {getVoiceLabelInitials(diarization.voice_label || diarization.label)}
                </Box>
              )}
            </DiarizationMarkerLabel>
          }
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          contentSx={{ p: 0, maxWidth: 'none' }}
          paperSx={{ minWidth: 280, maxWidth: 360 }}
          delay={150}
        >
          <DiarizationInfo diarization={diarization} color={color} />
        </HoverPopover>
      </Box>
    </StreamEl>
  );
}
